import React, { useMemo } from 'react';

import { IconBell } from '@/components/Icon/IconBell';
import { ChangeDataCenter } from '@/components/TopNav/components/ChangeModule';
import { useCurrentMenu } from '@/hooks/useMenu';
import { NAV_MAP } from '@/hooks/useMenu/constants';
import { useDataCenterSwitcherPlacement } from '@/hooks/useTenantDataCenterSwitcher';
import { cs } from '@/utils/className';

import styles from './index.module.less';

export interface PageBannerProps {
  /** 自定义类名 */
  className?: string;
  /** 自定义标题，不传则根据当前路由从菜单自动获取 */
  title?: React.ReactNode;
  /** 自定义描述，不传则根据当前路由从菜单自动获取 */
  description?: React.ReactNode;
}

const DESCRIPTION_ICON_STYLE: React.CSSProperties = {
  marginRight: 8,
  color: 'rgba(0, 0, 0, 0.45)',
  marginBottom: 2,
};

/**
 * 页面顶部横幅组件，用于展示当前页面的标题与描述。
 * 默认根据当前路由从菜单配置（NAV_MAP）自动获取 title 和 description，
 * 可通过 props 覆盖。
 */
function PageBanner({ className, title: titleProp, description: descriptionProp }: PageBannerProps) {
  const menuKey = useCurrentMenu();
  const dataCenterPlacement = useDataCenterSwitcherPlacement();
  const showDataCenterSwitcher = dataCenterPlacement === 'page-banner';

  const { title, description } = useMemo(() => {
    if (titleProp !== undefined || descriptionProp !== undefined) {
      return {
        title: titleProp ?? '',
        description: descriptionProp ?? '',
      };
    }
    if (!menuKey) return { title: '', description: '' };
    const nav = NAV_MAP[menuKey as keyof typeof NAV_MAP];
    return {
      title: (nav as { title?: string })?.title ?? menuKey,
      description: (nav as { description?: string })?.description ?? '',
    };
  }, [menuKey, titleProp, descriptionProp]);

  const displayTitle = title ?? '-';
  const displayDescription = description ?? '-';

  return (
    <div className={cs(styles.wrapper, className)}>
      <div className={styles.titleWrapper}>
        <div className={styles.titlebox}>
          <div className={styles.title} style={{ opacity: displayTitle ? 1 : 0 }}>
            {displayTitle}
            {showDataCenterSwitcher && (
              <div className={styles.dataCenterSwitcher}>
                <ChangeDataCenter
                  style={{ width: 'auto', flex: '0 0 auto', border: '1px solid var(--color-border-2, #e5e6eb)' }}
                  refreshCurrentPage
                />
              </div>
            )}
          </div>
          <p className={styles.textsecond} style={{ opacity: displayDescription ? 1 : 0 }}>
            <IconBell style={DESCRIPTION_ICON_STYLE} size="16px" />
            {displayDescription}
          </p>
        </div>
      </div>
    </div>
  );
}

export default React.memo(PageBanner);
